// Global Chart.js theme (RET-4289).
//
// Applied once when charts_js_controller is imported, so EVERY chart on every dashboard
// adopts the same baseline look — Inter type, a dark cyan-accented tooltip, point-style
// legends, softened grid/text, and brand auto-colors for unstyled series. Per-chart options
// still win (the controller merges them over Chart.js defaults via mergeDeepLeft), so this
// only sets the new baseline.
//
// Colors come from chartPalette() (charts/palette.js → custom/charts.scss CSS variables) so
// the canvas and the DOM (legend swatches, unit toggle) share one source of truth and stay
// correct for the user's active theme.
import { Chart } from 'chart.js'

import { chartPalette, categoricalColors, datasetThemingPlugin } from './palette'

let applied = false

export function applyGlobalChartDefaults() {
    if (applied) return // idempotent

    // The tooltip defaults only exist once the Tooltip plugin is registered (it is by first
    // chart connect, when this runs via charts_js_controller#initChart). Bail WITHOUT marking
    // applied, so a later call completes the theming rather than skipping it forever.
    if (!Chart.defaults.plugins || !Chart.defaults.plugins.tooltip) return
    applied = true

    const palette = chartPalette()

    Chart.defaults.color = palette.ink // Text color
    Chart.defaults.borderColor = palette.grid // Grid color
    Chart.defaults.elements.line.borderWidth = 2 // Thinner stroke than Chart.js' default of 3

    // Dark, cyan-bordered tooltip with point-style swatches — the design's look, applied
    // globally. A dark tooltip reads well over both light and dark dashboards.
    Object.assign(Chart.defaults.plugins.tooltip, {
        backgroundColor: palette.tooltipBg,
        borderColor: palette.cyan,
        borderWidth: 1,
        titleColor: '#ffffff',
        bodyColor: '#c5c9d0',
        padding: 10,
        cornerRadius: 4,
        usePointStyle: true,
        boxWidth: 8,
        boxHeight: 8,
        boxPadding: 6, // gap between the color swatch and the label text (Chart.js default is 1)
    })

    // Theme every chart's datasets: resolve var() color tokens, area-fill filled lines, and
    // auto-color unstyled series with the brand palette. The native `colors` plugin can't take
    // a custom palette AND it disables itself the moment Chart.defaults.borderColor is set
    // (above), so swap it for our own pipeline — otherwise bare multi-series bars/pies fall back
    // to Chart.js' faint rgba(0,0,0,0.1). See datasetThemingPlugin.
    if (Chart.defaults.plugins.colors) Chart.defaults.plugins.colors.enabled = false
    Chart.register(datasetThemingPlugin(categoricalColors(palette)))
}
